import { Button } from '@anyroad/button';
import type { FormEventHandler, ReactNode } from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useState,
  useReducer,
} from 'react';
import styled from '@emotion/styled';
import { useStripeContext } from './StripeContext';
import {
  useElements,
  PaymentElement,
  useStripe,
  ExpressCheckoutElement,
} from '@stripe/react-stripe-js';
import type {
  Layout,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js';
import { ConfigProvider, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

const Form = styled.form({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
});

type CheckoutFormProps = {
  /** Custom button content to render while not loading. Will be wrapped in the default Button component with proper disabled state and loading behavior. */
  buttonContent?: ReactNode;
  /** Theme configuration for the button's ConfigProvider. Default: { colorPrimary: '#ffffff' } */
  buttonTheme?: {
    colorPrimary?: string;
  };
  /** Custom spinner to render while loading instead of default LoadingOutlined */
  customSpinner?: ReactNode;
  dataTestId?: string;
  /** The data-testid attribute. */
  defaultValues?: {
    email?: string | null;
    name?: string | null;
  };
  /** Whether to show the Express Checkout Element (Apple Pay, Google Pay, etc.). Default: true */
  enableExpressCheckout?: boolean;
  /** Text to show in divider between express checkout and regular payment. Default: 'Or pay with card' */
  expressCheckoutDividerText?: string;
  /** Whether to hide the built-in submit button. Use this when you want to handle submission from a parent component. Default: false */
  hideButton?: boolean;
  localizationStrings: {
    errorMessage: string;
    processingMessage: string;
    requiresPaymentMethodMessage: string;
    submitButtonText: string;
    successMessage: string;
  };
  /** Optional callback executed before payment confirmation. Can be used to create/attach customer. Should return a Promise that resolves when ready to proceed. */
  onBeforeConfirm?: () => Promise<void>;
  /** Size of the default spinner in pixels. Default: 26 */
  spinnerSize?: number;
};
type withReturnUrl = CheckoutFormProps & {
  onIntentConfirm?: never;
  returnUrl: string;
};
type withCallback = CheckoutFormProps & {
  onIntentConfirm: () => void;
  returnUrl?: never;
};
type PropType = withCallback | withReturnUrl;

const ButtonContainer = styled.div({
  padding: '16px 0',
  display: 'flex',
  justifyContent: 'center',
  marginTop: '16px',
});

const StripeElementContainer = styled.div({
  width: '100%',
  padding: '0',
  marginBottom: '16px',
});

const ExpressCheckoutContainer = styled.div({
  width: '100%',
  marginBottom: '24px',
});

const Divider = styled.div({
  display: 'flex',
  alignItems: 'center',
  textAlign: 'center',
  margin: '24px 0',
  '&::before, &::after': {
    content: '""',
    flex: 1,
    borderBottom: '1px solid #E0E0E0',
  },
  '&::before': {
    marginRight: '16px',
  },
  '&::after': {
    marginLeft: '16px',
  },
});

const DividerText = styled.span({
  color: '#666',
  fontSize: '14px',
  fontWeight: 500,
});

/**
 * CheckoutForm component.
 *
 * @type {React.FC<StripeUiProps>}
 * @returns {React.ReactElement} The stripe-ui.
 * @example
 * import { CheckoutForm } from './StripeUi';
 */
export const CheckoutForm = forwardRef<HTMLFormElement, PropType>(
  (
    {
      buttonTheme = { colorPrimary: '#ffffff' },
      buttonContent,
      customSpinner,
      dataTestId,
      defaultValues,
      enableExpressCheckout = true,
      expressCheckoutDividerText = 'Or pay with card',
      hideButton = false,
      localizationStrings,
      onIntentConfirm,
      returnUrl,
      spinnerSize = 26,
    },
    ref
  ) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showExpressCheckout, setShowExpressCheckout] = useState(false);

    const { clientSecret } = useStripeContext();
    const stripe = useStripe();
    const elements = useElements();

    const [{ message }, dispatch] = useReducer(
      (
        state: { message: string },
        action: { message?: string; type: string }
      ) => {
        switch (action.type) {
          case 'succeeded':
            return { ...state, message: localizationStrings.successMessage };
          case 'processing':
            return { ...state, message: localizationStrings.processingMessage };
          case 'requires_payment_method':
            return {
              ...state,
              message: localizationStrings.requiresPaymentMethodMessage,
            };
          case 'errored':
            return { ...state, message: localizationStrings.errorMessage };
          case 'card_error':
            return { ...state, message: action.message || '' };
          case 'validation_error':
            return { ...state, message: action.message || '' };
          default:
            return { ...state, message: '' };
        }
      },
      {
        message: '',
      }
    );

    useEffect(() => {
      if (!stripe || !clientSecret) {
        return;
      }

      stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
        if (paymentIntent) {
          dispatch({ type: paymentIntent.status });
        }
      });
    }, [stripe, clientSecret]);

    const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
      async (e) => {
        e.preventDefault();

        if (!stripe || !elements) return;

        setIsLoading(true);
        const confirmParams: {
          payment_method_data?: {
            billing_details: {
              email?: string;
            };
          };
          return_url: string;
        } = {
          return_url: returnUrl || '',
        };

        if (defaultValues?.email) {
          confirmParams.payment_method_data = {
            billing_details: {
              email: defaultValues.email,
            },
          };
        }

        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          confirmParams,
          redirect: 'if_required',
        });
        if (error) {
          if (
            error.type === 'card_error' ||
            error.type === 'validation_error'
          ) {
            dispatch({ type: error.type, message: error.message });
          } else {
            dispatch({ type: 'errored' });
          }
          setIsLoading(false);
        } else if (paymentIntent?.status === 'succeeded') {
          if (onIntentConfirm) {
            onIntentConfirm();
            setIsLoading(false);
            if (returnUrl) {
              window.location.href = returnUrl;
            }
          }
        } else {
          setIsLoading(false);
        }
      },
      [stripe, elements, returnUrl, onIntentConfirm, defaultValues]
    );

    const handleExpressCheckoutConfirm = useCallback(async () => {
      if (!stripe || !elements) {
        return;
      }

      setIsLoading(true);

      const submitResult = await elements.submit();
      if (submitResult?.error) {
        setIsLoading(false);
        return;
      }

      const confirmParams: {
        payment_method_data?: {
          billing_details: {
            email: string;
          };
        };
        return_url: string;
      } = {
        return_url: returnUrl || '',
      };

      if (defaultValues?.email && defaultValues.email.trim() !== '') {
        confirmParams.payment_method_data = {
          billing_details: {
            email: defaultValues.email.trim(),
          },
        };
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: clientSecret,
        confirmParams,
        redirect: 'if_required',
      });

      if (error) {
        if (error.type === 'card_error' || error.type === 'validation_error') {
          dispatch({ type: error.type, message: error.message });
        } else {
          dispatch({ type: 'errored' });
        }
        setIsLoading(false);
      } else if (paymentIntent?.status === 'succeeded') {
        if (onIntentConfirm) {
          onIntentConfirm();
          setIsLoading(false);
        } else if (returnUrl) {
          window.location.href = returnUrl;
        }
      } else {
        setIsLoading(false);
      }
    }, [
      stripe,
      elements,
      clientSecret,
      returnUrl,
      onIntentConfirm,
      defaultValues,
    ]);

    const onExpressCheckoutReady = useCallback(
      (event: StripeExpressCheckoutElementReadyEvent) => {
        if (event.availablePaymentMethods) {
          setShowExpressCheckout(true);
        }
      },
      []
    );

    const expressCheckoutOptions = {
      wallets: {
        applePay: 'auto' as const,
        googlePay: 'auto' as const,
      },
    };

    const paymentElementOptions = {
      layout: 'tabs' as Layout,
      defaultValues: {
        billingDetails: {
          name: defaultValues?.name ?? '',
        },
      },
      fields: {
        billingDetails: {
          email:
            defaultValues?.email && defaultValues.email.trim() !== ''
              ? ('never' as const)
              : ('auto' as const),
        },
      },
      terms: {
        card: 'never' as const,
      },
    };

    return (
      <Form
        data-testid={dataTestId || 'payment-form'}
        onSubmit={onSubmit}
        ref={ref}
      >
        {Boolean(enableExpressCheckout) && (
          <>
            <ExpressCheckoutContainer>
              <ExpressCheckoutElement
                onConfirm={handleExpressCheckoutConfirm}
                onReady={onExpressCheckoutReady}
                options={expressCheckoutOptions}
              />
            </ExpressCheckoutContainer>
            {Boolean(showExpressCheckout) && (
              <Divider>
                <DividerText>{expressCheckoutDividerText}</DividerText>
              </Divider>
            )}
          </>
        )}
        <StripeElementContainer>
          <PaymentElement
            data-testid="payment-element"
            id="payment-element"
            options={paymentElementOptions}
          />
          {!hideButton && (
            <ConfigProvider
              theme={{
                token: buttonTheme,
              }}
            >
              <ButtonContainer>
                <Button
                  color="primary"
                  data-testid="complete-payment"
                  disabled={isLoading || !stripe || !elements}
                  id="submit"
                  type="submit"
                >
                  {isLoading
                    ? customSpinner || (
                        <Spin
                          indicator={
                            <LoadingOutlined
                              spin
                              style={{ fontSize: spinnerSize }}
                            />
                          }
                        />
                      )
                    : buttonContent || (
                        <span id="button-text">
                          {localizationStrings.submitButtonText}
                        </span>
                      )}
                </Button>
              </ButtonContainer>
            </ConfigProvider>
          )}
        </StripeElementContainer>

        {Boolean(message) && (
          <div data-testid="payment-message" id="payment-message">
            {message}
          </div>
        )}
      </Form>
    );
  }
);

CheckoutForm.displayName = 'CheckoutForm';
